import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useRef, useState, useMemo } from "react";
import { Metric, Objective } from "./AppTypes";
import { notifyProjectActivity } from "./api/pingClient";
import { getBaseColor, Mode } from "./colorUtils";
import { makeId } from "./xmlHandling";

const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (v: string) =>
  SPECIAL_CHARACTERS.reduce((s, c) => s.split(c).join(""), v);

type Props = {
  objectives: Objective[];
  onChange: (objectives: Objective[]) => void;
  metrics: Metric[];
  allNames?: string[];
  dimensions?: any;
  syntaxData?: any;
  isActive: boolean;
  toggle: () => void;
  userHue: number;
  mode: Mode;
  projectId: number;
};

// ─────────────────────────────────────────────────────────────────────────────
// ObjectiveCard
// ─────────────────────────────────────────────────────────────────────────────
function ObjectiveCard({
  objective,
  isOverlay,
  expandedId,
  setExpandedId,
  onUpdate,
  metrics,
  userHue,
  mode,
  allNames,
  dragAttributes,
  dragListeners,
  dragRef,
  dragStyle,
}: {
  objective: Objective;
  isOverlay?: boolean;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  onUpdate: (id: string, updates: Partial<Objective>) => void;
  metrics: Metric[];
  userHue: number;
  mode: Mode;
  allNames?: string[];
  dragAttributes?: any;
  dragListeners?: any;
  dragRef?: (node: HTMLElement | null) => void;
  dragStyle?: React.CSSProperties;
}) {
  const expanded = expandedId === objective.id && !isOverlay;
  const isDuplicate = (allNames || []).filter((n: string) => n === objective.name).length > 1;
  const [isEditingName, setIsEditingName] = useState(false);
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(null);

  const cardBg = getBaseColor(3, "distinct", userHue, mode);

  return (
    <div
      ref={dragRef}
      className={`objective-card ${isOverlay ? "objective-card--overlay" : ""}`}
      style={{
        ...dragStyle,
        "--metric-card-bg": cardBg,
      } as React.CSSProperties}
      {...dragAttributes}
    >
      {/* ── Header ── */}
      <div className="objective-card__header">
        <div
          className="objective-card__drag"
          {...dragListeners}
          title="Drag to reorder"
        >
          ☰
        </div>

        <button
          className="objective-card__toggle"
          type="button"
          onClick={() => !isOverlay && setExpandedId(expanded ? null : objective.id)}
        >
          {expanded ? "▾" : "▸"}
        </button>

        {isEditingName ? (
          <input
            className={`objective-card__name-input ${isDuplicate ? "objective-card__name-input--duplicate" : ""}`}
            type="text"
            value={objective.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              const stripped = stripSpecialChars(raw);
              if (stripped !== raw) {
                const found = SPECIAL_CHARACTERS
                  .filter((c) => raw.indexOf(c) !== -1)
                  .map((c) => (c === "\n" ? "\u21b5" : c))
                  .join(", ");
                setSpecialCharWarning(found);
                setTimeout(() => setSpecialCharWarning(null), 2000);
              }
              onUpdate(objective.id, { name: stripped });
            }}
            onBlur={() => setIsEditingName(false)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") setIsEditingName(false);
            }}
            autoFocus
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          />
        ) : (
          <div
            className="objective-card__title"
            onDoubleClick={() => !isOverlay && setIsEditingName(true)}
          >
            <span>{objective.name}</span>
            {isDuplicate && (
              <span className="objective-card__duplicate">Name already in use</span>
            )}
          </div>
        )}
        
        {specialCharWarning && (
          <span style={{ color: "#e55", fontSize: "0.75em", marginLeft: "8px" }}>
            "{specialCharWarning}" nicht erlaubt
          </span>
        )}
      </div>

      {/* ── Expanded ── */}
      {expanded && !isOverlay && (
        <div
          className="objective-card__body"
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
        >
          <div className="objective-card__controls">
            <label className="metric-field">
              <span>Type</span>
              <select
                value={objective.type}
                onChange={(e) => onUpdate(objective.id, { type: e.target.value as Objective["type"] })}
              >
                <option value="minimize">minimize</option>
                <option value="maximize">maximize</option>
                <option value="target">target</option>
                <option value="balance">balance</option>
              </select>
            </label>

            <label className="metric-field">
              <span>Metric</span>
              <select
                value={objective.metric_ref}
                onChange={(e) => onUpdate(objective.id, { metric_ref: e.target.value })}
              >
                <option value="">-- select metric --</option>
                {metrics.map(m => (
                  <option key={m.id} value={m.name}>{m.name}</option>
                ))}
              </select>
            </label>

            {objective.type === "target" && (
              <label className="metric-field">
                <span>Target</span>
                <input
                  type="number"
                  min="0"
                  value={objective.target || ""}
                  onChange={(e) => onUpdate(objective.id, { target: e.target.value })}
                  onKeyDown={(e) => {
                    if (["-", "+", "e", "E", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  style={{ width: 100 }}
                />
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SortableObjectiveCard
// ─────────────────────────────────────────────────────────────────────────────
function SortableObjectiveCard(
  props: Omit<
    React.ComponentProps<typeof ObjectiveCard>,
    "dragAttributes" | "dragListeners" | "dragRef" | "dragStyle"
  >
) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.objective.id,
  });
  return (
    <ObjectiveCard
      {...props}
      dragRef={setNodeRef}
      dragAttributes={attributes}
      dragListeners={listeners}
      dragStyle={{ transform: CSS.Transform.toString(transform), transition }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ObjectiveEditor – default export
// ─────────────────────────────────────────────────────────────────────────────
export default function ObjectiveEditor({
  objectives,
  onChange,
  metrics,
  allNames,
  isActive,
  toggle,
  userHue,
  mode,
  projectId,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const editorFrameRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const handleAdd = () => {
    const newObj: Objective = {
      id: makeId("objective"),
      name: `Objective${objectives.length + 1}`,
      type: "minimize",
      metric_ref: metrics.length > 0 ? metrics[0].name : "",
    };
    onChange([...objectives, newObj]);
    setExpandedId(newObj.id);
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleUpdate = (id: string, updates: Partial<Objective>) => {
    onChange(objectives.map((o) => (o.id === id ? { ...o, ...updates } : o)));
    notifyProjectActivity(projectId).catch(() => {});
  };

  const handleDelete = (id: string) => {
    onChange(objectives.filter((o) => o.id !== id));
    if (expandedId === id) setExpandedId(null);
    notifyProjectActivity(projectId).catch(() => {});
  };

  const activeObjective = objectives.find((o) => o.id === activeId) ?? null;

  return (
    <div
      ref={editorFrameRef}
      className="objective-editor"
      style={{
        background: getBaseColor(2, "section", userHue, mode),
      }}
    >
      {/* Header */}
      <div
        className="objective-editor__header"
        onClick={toggle}
      >
        <div className="objective-editor__title">
          {isActive ? "▾" : "▸"} Objectives
        </div>
      </div>

      {isActive && (
        <div className="objective-editor__body">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => {
              setActiveId(String(e.active.id));
              setExpandedId(null);
            }}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              setActiveId(null);

              // Drag out → delete
              const finalRect = active.rect.current.translated ?? active.rect.current.initial;
              const editorRect = editorFrameRef.current?.getBoundingClientRect();
              if (
                finalRect && editorRect &&
                (finalRect.right <= editorRect.left ||
                  finalRect.left >= editorRect.right ||
                  finalRect.bottom <= editorRect.top ||
                  finalRect.top >= editorRect.bottom)
              ) {
                handleDelete(String(active.id));
                return;
              }

              if (active.id !== over?.id) {
                const oldIndex = objectives.findIndex((o) => o.id === active.id);
                const newIndex = objectives.findIndex((o) => o.id === over?.id);
                if (oldIndex !== -1 && newIndex !== -1) {
                  onChange(arrayMove(objectives, oldIndex, newIndex));
                }
              }
            }}
          >
            <SortableContext
              items={objectives.map((o) => o.id)}
              strategy={verticalListSortingStrategy}
            >
              {objectives.map((objective) => (
                <SortableObjectiveCard
                  key={objective.id}
                  objective={objective}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  onUpdate={handleUpdate}
                  metrics={metrics}
                  allNames={allNames}
                  userHue={userHue}
                  mode={mode}
                />
              ))}
            </SortableContext>

            <DragOverlay>
              {activeObjective ? (
                <ObjectiveCard
                  objective={activeObjective}
                  isOverlay
                  expandedId={null}
                  setExpandedId={() => {}}
                  onUpdate={() => {}}
                  metrics={metrics}
                  allNames={allNames}
                  userHue={userHue}
                  mode={mode}
                />
              ) : null}
            </DragOverlay>
          </DndContext>

          <button type="button" onClick={handleAdd} className="objective-editor__add">
            + Objective
          </button>
        </div>
      )}
    </div>
  );
}
